import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { apiService } from '../../../services/api';
import { useToast } from '../../../contexts/ToastContext';
import { useConfirm } from '../../../contexts/ConfirmContext';
import { AliasRow, ROLE_GROUP_LABEL, TagRow } from './types';
import * as fmt from '../../../lib/format';

const PRESS = 'transition-[transform,background-color,color,box-shadow] duration-150 active:scale-[0.98] motion-reduce:transform-none';
const INPUT =
  'rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-500 shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100';
const GROUPS = ['engineering', 'qa', 'product', 'design', 'devops'];

interface Props {
  tags: TagRow[];
  aliases: AliasRow[];
  onChanged: () => void;
}

// The standard worklog tags and the loose spellings that point at them. A tag is what an entry must
// start with; a spelling only helps the fix list suggest the right tag. Removing a tag is confirmed,
// with how often it was used, because entries that used it will then read as an unknown tag.
export default function TagsEditor({ tags, aliases, onChanged }: Props) {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [name, setName] = useState('');
  const [group, setGroup] = useState('engineering');
  const [description, setDescription] = useState('');
  const [alias, setAlias] = useState('');
  const [aliasTag, setAliasTag] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<any>, done: string, after?: () => void) => {
    setBusy(true);
    try {
      const r = await action();
      if (!r.success) throw new Error(r.error || 'Could not save');
      showToast('success', done);
      after?.();
      onChanged();
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const addTag = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    void run(() => apiService.addStandardTag(name.trim(), group, description.trim() || undefined), `Added the tag ${name.trim()}.`, () => {
      setName('');
      setDescription('');
    });
  };

  const removeTag = async (t: TagRow) => {
    const spellings = aliases.filter((a) => a.tag === t.tag).length;
    const used = t.used_90d > 0 ? `It was used ${fmt.count(t.used_90d)} times in the last 90 days; those entries will read as an unknown tag. ` : '';
    const folded = spellings > 0 ? `Its ${spellings} ${spellings === 1 ? 'spelling goes' : 'spellings go'} with it. ` : '';
    const yes = await confirm({ title: `Remove the tag ${t.tag}?`, message: `${used}${folded}`.trim() || undefined, confirmLabel: 'Remove tag' });
    if (yes) void run(() => apiService.removeStandardTag(t.tag), `Removed the tag ${t.tag}.`);
  };

  const addAlias = (e: React.FormEvent) => {
    e.preventDefault();
    const target = aliasTag || tags[0]?.tag;
    if (!alias.trim() || !target || busy) return;
    void run(() => apiService.addStandardAlias(alias.trim(), target), `"${alias.trim().toLowerCase()}" now suggests ${target}.`, () => setAlias(''));
  };

  const byGroup = GROUPS.map((g) => ({ group: g, tags: tags.filter((t) => t.role_group === g) })).filter((g) => g.tags.length > 0);

  return (
    <div className="space-y-6">
      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200/70" aria-labelledby="std-tags">
        <h3 id="std-tags" className="text-sm font-semibold text-gray-900">Standard tags</h3>
        <p className="mt-0.5 text-xs text-gray-500">A worklog entry should start with one of these in square brackets, for example [Dev]. The Time logging pages and new entries follow changes straight away.</p>

        <div className="mt-4 space-y-4">
          {byGroup.map(({ group: g, tags: list }) => (
            <div key={g}>
              <h4 className="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">{ROLE_GROUP_LABEL[g]}</h4>
              <ul className="mt-1.5 flex flex-wrap gap-2">
                {list.map((t) => (
                  <li key={t.tag} className="inline-flex items-center overflow-hidden rounded-full bg-gray-50 text-sm text-gray-800 ring-1 ring-inset ring-gray-200">
                    <span className="py-1 pl-3 pr-1" title={t.description ?? undefined}>
                      {t.tag}
                      <span className="ml-1.5 text-xs tabular-nums text-gray-500" aria-label={`used ${t.used_90d} times in the last 90 days`}>{fmt.count(t.used_90d)}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeTag(t)}
                      disabled={busy}
                      aria-label={`Remove the tag ${t.tag}`}
                      className={`px-2 py-1 text-gray-500 hover:bg-rose-50 hover:text-rose-600 ${PRESS} focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-rose-300`}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-gray-500">The small number is how often the tag was used in the last 90 days.</p>

        <form onSubmit={addTag} className="mt-5 flex flex-wrap items-end gap-2 border-t border-gray-100 pt-4">
          <label className="text-xs font-medium text-gray-600">
            New tag
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Pairing" maxLength={40} className={`${INPUT} mt-1 block w-40`} />
          </label>
          <label className="text-xs font-medium text-gray-600">
            For
            <select value={group} onChange={(e) => setGroup(e.target.value)} className={`${INPUT} mt-1 block`}>
              {GROUPS.map((g) => (
                <option key={g} value={g}>{ROLE_GROUP_LABEL[g]}</option>
              ))}
            </select>
          </label>
          <label className="min-w-[12rem] flex-1 text-xs font-medium text-gray-600">
            What it means (optional)
            <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} className={`${INPUT} mt-1 block w-full`} />
          </label>
          <button type="submit" disabled={!name.trim() || busy} className={`inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none ${PRESS} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400`}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add tag
          </button>
        </form>
      </section>

      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200/70" aria-labelledby="std-aliases">
        <h3 id="std-aliases" className="text-sm font-semibold text-gray-900">Loose spellings</h3>
        <p className="mt-0.5 text-xs text-gray-500">When an entry starts with one of these, the fix list suggests the tag it points at, for example "[retesting]" suggests [Bug Verification]. A hint only; the entry still has to be corrected in Jira.</p>

        <ul className="mt-4 grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {aliases.map((a) => (
            <li key={a.alias} className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-1.5 text-sm ring-1 ring-inset ring-gray-100">
              <span className="min-w-0 truncate text-gray-800">
                <span className="font-mono text-xs">{a.alias}</span>
                <span className="mx-1.5 text-gray-500" aria-hidden>→</span>
                <span className="sr-only"> suggests </span>
                {a.tag}
              </span>
              <button
                type="button"
                onClick={() => void run(() => apiService.removeStandardAlias(a.alias), `Removed the spelling "${a.alias}".`)}
                disabled={busy}
                aria-label={`Remove the spelling ${a.alias}`}
                className={`rounded p-1 text-gray-500 hover:bg-rose-50 hover:text-rose-600 ${PRESS} focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300`}
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>

        <form onSubmit={addAlias} className="mt-5 flex flex-wrap items-end gap-2 border-t border-gray-100 pt-4">
          <label className="text-xs font-medium text-gray-600">
            Spelling
            <input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="pair programming" maxLength={60} className={`${INPUT} mt-1 block w-48`} />
          </label>
          <label className="text-xs font-medium text-gray-600">
            Suggests
            <select value={aliasTag || tags[0]?.tag || ''} onChange={(e) => setAliasTag(e.target.value)} className={`${INPUT} mt-1 block`}>
              {tags.map((t) => (
                <option key={t.tag} value={t.tag}>{t.tag}</option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={!alias.trim() || busy} className={`inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none ${PRESS} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400`}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add spelling
          </button>
        </form>
      </section>
    </div>
  );
}
